import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { PortalIcon } from '@/components/portal/PortalIcon';
import { usePortalConsentDocumentPrompts, usePortalConsentInbox } from '@/hooks/useConsentRequests';
import { PORTAL_CONSENT_PROMPT_DISMISSED_KEY, PORTAL_CONSENT_TYPE_LABELS } from '@/lib/consent/portalConsent';
import { usePortalActiveClinic } from '@/lib/portalActiveClinic';

function readDismissed(): boolean {
  try {
    return window.sessionStorage.getItem(PORTAL_CONSENT_PROMPT_DISMISSED_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * Shown once per browser session when consent requests or a new privacy
 * notice / portal terms version are waiting for the logged-in portal user.
 */
export function PortalConsentPrompt() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(readDismissed);
  const onConsentPage = pathname.startsWith('/portal/consents');
  const { activeClinicId } = usePortalActiveClinic();
  const inbox = usePortalConsentInbox(!dismissed, activeClinicId);
  const prompts = usePortalConsentDocumentPrompts(!dismissed, activeClinicId);

  const requestCount = new Set((inbox.data ?? []).map((row) => row.request_id)).size;
  const documentRows = prompts.data ?? [];
  if (dismissed || onConsentPage || (requestCount === 0 && documentRows.length === 0)) return null;

  const dismiss = () => {
    try {
      window.sessionStorage.setItem(PORTAL_CONSENT_PROMPT_DISMISSED_KEY, '1');
    } catch {
      // Private mode: the prompt then only stays hidden until reload.
    }
    setDismissed(true);
  };

  const documentLabels = [...new Set(documentRows.map((row) => PORTAL_CONSENT_TYPE_LABELS[row.consent_type]))];

  return (
    <>
      <div className="scrim" onClick={dismiss} />
      <div className="modal" role="dialog" aria-modal="true" aria-labelledby="consent-prompt-title">
        <div className="grab" />
        <div className="mh">
          <div>
            <h2 id="consent-prompt-title">Klinikken beder dig tage stilling</h2>
            <div className="help">Det tager kun et øjeblik, og du kan læse teksten først.</div>
          </div>
          <button type="button" className="btn btn-tertiary btn-icon x" aria-label="Luk" onClick={dismiss}>
            <PortalIcon name="x" />
          </button>
        </div>
        <div className="mb">
          <ul className="help" style={{ display: 'grid', gap: 8 }}>
            {requestCount > 0 ? (
              <li>
                {requestCount === 1
                  ? 'Du har 1 anmodning om samtykke, der venter på dit svar.'
                  : `Du har ${requestCount} anmodninger om samtykke, der venter på dit svar.`}
              </li>
            ) : null}
            {documentLabels.map((label) => (
              <li key={label}>{label} er opdateret og skal læses.</li>
            ))}
          </ul>
        </div>
        <div className="mf">
          <button type="button" className="btn btn-secondary" onClick={dismiss}>
            Senere
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => {
              dismiss();
              navigate('/portal/consents');
            }}
          >
            Se og svar
          </button>
        </div>
      </div>
    </>
  );
}
